// Една кутия на таблото: номерът (клавиш), иконата и името на операцията, живото
// ѝ състояние и основните действия. Цялата кутия отваря операцията — заглавието
// е ИСТИНСКИ бутон, разпънат върху кутията (`after:inset-0`), така че кликът
// където и да е, Tab, Enter и екранният четец виждат едно и също; бутоните с
// действия стоят над него (`z-10`). Фокусът на заглавието рисува рамката на
// цялата кутия (`has-[…:focus-visible]`).
//
// Състояния: зарежда (скелет) · готово · не се зареди (кутията пак се отваря)
// · изключено в локален режим. Сигналът на кутията (`attentionLevelOf`):
// критично — червена рамка, черта 3px вляво и червена точка до името (съвпада с
// червения текст „1 закъснял" вътре); внимание — тънка кехлибарена рамка и
// кехлибарена точка.
import { useId, type ComponentType, type ReactNode } from "react";
import { ArrowUpOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { KbdChip } from "../../components/KbdChip";
import type { DashboardBoxSlug, DashboardSignal, Loadable } from "../dashboardModel";

export type DashboardBoxView = {
  slug: DashboardBoxSlug;
  // краткото име в кутията („Плащане") и пълното от менюто („Плащане на доставки")
  title: string;
  label: string;
  Icon: ComponentType<{ className?: string }>;
  state: Loadable<unknown>["state"];
  signal: DashboardSignal | null;
  // голямото число с думите след него / изречение при „всичко е наред"
  headline?: ReactNode;
  // редът под него (сума, най-стар, склад)
  detail?: ReactNode;
  actions?: ReactNode;
  onOpen: () => void;
};

type Props = {
  box: DashboardBoxView;
  index: number;
  showKey: boolean;
};

const SIGNAL_FRAME_CLASS: Record<DashboardSignal | "none", string> = {
  critical: "border-danger/60 border-l-[3px] border-l-danger",
  attention: "border-brand/50",
  none: "border-shade-2",
};

const SIGNAL_DOT_CLASS: Record<DashboardSignal, string> = {
  critical: "bg-danger",
  attention: "bg-brand",
};

const BoxSkeleton = () => (
  <div aria-hidden className="flex flex-col gap-2 pt-0.5">
    <span className="h-6 w-28 animate-pulse rounded-md bg-fill-2" />
    <span className="h-3.5 w-40 animate-pulse rounded bg-fill-2" />
  </div>
);

export const DashboardBox = ({ box, index, showKey }: Props) => {
  const { t } = useTranslation();
  const bodyId = useId();
  const { Icon } = box;
  const key = String(index + 1);
  const signalLabel =
    box.signal === "critical"
      ? t("inventoryPage.dashCritical")
      : box.signal === "attention"
        ? t("inventoryPage.dashAttention")
        : null;

  const body =
    box.state === "loading" ? (
      <BoxSkeleton />
    ) : box.state === "error" ? (
      <span className="text-[13px] text-muted">{t("inventoryPage.dashLoadFailed")}</span>
    ) : box.state === "off" ? (
      <span className="font-sheet text-[22px] leading-none text-faint">—</span>
    ) : (
      <>
        {box.headline}
        {box.detail && (
          <span className="text-[12.5px] leading-[1.4] text-muted">{box.detail}</span>
        )}
      </>
    );

  return (
    <article
      data-dash-box={box.slug}
      data-dash-signal={box.signal ?? undefined}
      className={`group relative flex min-w-0 flex-col gap-3 rounded-xl border bg-panel px-[18px] py-4 transition-colors hover:border-shade-4 has-[[data-dash-open]:focus-visible]:border-brand has-[[data-dash-open]:focus-visible]:shadow-[0_0_0_3px_var(--color-brand-tint)] ${
        SIGNAL_FRAME_CLASS[box.signal ?? "none"]
      }`}
    >
      <button
        type="button"
        data-dash-open
        data-dash-index={index}
        onClick={box.onOpen}
        aria-label={
          signalLabel
            ? `${t("inventoryPage.dashOpenAria", { name: box.label })} · ${signalLabel}`
            : t("inventoryPage.dashOpenAria", { name: box.label })
        }
        aria-describedby={bodyId}
        aria-keyshortcuts={key}
        title={box.label}
        className="flex w-full min-w-0 cursor-pointer items-center gap-2.5 border-none bg-transparent p-0 text-left outline-none after:absolute after:inset-0 after:rounded-xl after:content-['']"
      >
        {showKey && <KbdChip>{key}</KbdChip>}
        <span className="inline-flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-brand-tint text-brand-deep-ink">
          <Icon className="text-[14px]" />
        </span>
        <span className="min-w-0 flex-1 truncate text-[15px] font-semibold leading-[1.2] text-ink">
          {box.title}
        </span>
        {box.signal && (
          <span aria-hidden className={`h-2 w-2 shrink-0 rounded-full ${SIGNAL_DOT_CLASS[box.signal]}`} />
        )}
        <ArrowUpOutlined className="shrink-0 rotate-45 text-[12px] text-faint transition-colors group-hover:text-ink" />
      </button>

      <div id={bodyId} className="flex min-h-[52px] flex-1 flex-col justify-center gap-1">
        {body}
      </div>

      {box.actions && box.state !== "off" && (
        <div className="flex flex-wrap items-center gap-2">{box.actions}</div>
      )}
    </article>
  );
};

export default DashboardBox;
